import { BandData, BandType } from 'src/app/interfaces/band-models.interface';
import { Node, NodeData, NodeType } from 'src/app/interfaces/node-models.interface';
import {
	ALREADY_SAVED_STATUS,
	VALID_SAVE_STATUS,
	DEEP_NODES_STATUS,
	GRID_SIZE,
	MULTIPLE_WORKFLOWS_STATUS,
	NO_WORKFLOWS_STATUS,
	UNREACHABLE_NODES_STATUS,
	EMPTY_WORKSPACE,
} from './workspace.const';
import { Workspace, CoreWorkspaceStatus, WorkspaceStatus } from 'src/app/interfaces/workspace.interface';
import { ObjID } from 'src/app/interfaces/object-id.interface';
import { MAX_FAILOVER_DEPTH, MAX_CHAIN_DEPTH } from 'src/environments/environment';
import { NodeModel, Product } from 'src/app/interfaces/backend-models.interface';
import { Graph } from 'src/app/utils/graph/graph.utils';
import { getUUID } from 'src/app/utils/id-utils/id.utils';
import { cloneDeep } from 'lodash';
import { Point } from '@angular/cdk/drag-drop';
import { NodeSetModel, WorkflowCardData } from 'src/app/interfaces/workflow.interface';

let availableNodes: Map<ObjID, NodeData> = new Map();

export const UNTITLED_GROUP_TEXT = $localize`Untitled Group`;
export const DELETED_NODE_APP_NAME = $localize`Unknown`;
export const DELETED_NODE_NAME = $localize`Node deleted`;

export function interpretWorkspaceAbilities(status: CoreWorkspaceStatus): WorkspaceStatus {
	return {
		...status,
		saveEnabled: status.modified && status.valid,
	};
}

/**
 * Determine whether the current workspace is valid and has been modified since the last save
 * Return the workspace status and a list of invalid nodes if there are any
 * @param workspace - current workspace (unmapped nodes, bands and workspaceId)
 * @param lastSavedWorkspace - last saved workspace to compare with
 * @param graph - Data structure representing nodes, bands, and workflows
 */
export function getDeployStatus(
	workspace: Readonly<Workspace>,
	lastSavedWorkspace: Readonly<Workspace>
): [CoreWorkspaceStatus, ObjID[]] {
	let status: CoreWorkspaceStatus = { valid: true, modified: true, message: '' };
	let nodesToMark: ObjID[] = [];
	let unreachableNodes: Set<ObjID> = new Set();

	const graph = new Graph(workspace);

	// Check for actions that are chained too deep
	const checkDepthResp = checkDepth(graph);
	nodesToMark = nodesToMark.concat(checkDepthResp[0]);
	unreachableNodes = new Set(
		Array.from(checkDepthResp[1].entries())
			.filter(x => !x[1])
			.map(x => x[0])
	);
	nodesToMark = nodesToMark.concat(Array.from(unreachableNodes));
	if (checkDepthResp[0].length) {
		status.message = status.message.concat(DEEP_NODES_STATUS.message);
		status.valid = false;
	}

	if (unreachableNodes.size) {
		status.message = status.message.concat(UNREACHABLE_NODES_STATUS.message);
		status.valid = false;
	}

	// Mark children of invalid nodes as invalid
	for (const id of nodesToMark) {
		nodesToMark = nodesToMark.concat((graph.getNode(id)?.children || []).map(x => x.id));
	}

	// Remove duplicate entries from the list of invalid nodes
	nodesToMark = Array.from(new Set(nodesToMark));

	// Check if there are multiple workflows
	if (workspace.workflows.length > 1) {
		status.message = status.message.concat(MULTIPLE_WORKFLOWS_STATUS.message);
		status.valid = false;
		for (const workflow of workspace.workflows.slice(1)) {
			nodesToMark = nodesToMark.concat(Array.from(workflow.nodes));
		}
	}

	// Check if there have been any changes
	if (status.valid && workspacesAreSame(workspace, lastSavedWorkspace)) {
		status = { ...ALREADY_SAVED_STATUS };
		return [status, nodesToMark];
	}

	// Check if there are no workflows
	if (workspace.workflows.length < 1) {
		status.message = status.message.concat(NO_WORKFLOWS_STATUS.message);
		status.valid = false;
	}

	// Save is valid
	if (!status.message) {
		status = { ...VALID_SAVE_STATUS };
	}

	return [status, nodesToMark];
}

export function getClosestGridPoint(point: Point): Point {
	return {
		x: Math.round(point.x / GRID_SIZE) * GRID_SIZE,
		y: Math.round(point.y / GRID_SIZE) * GRID_SIZE,
	};
}

function workspacesAreSame(workspace1: Workspace, workspace2: Workspace): boolean {
	return getWorkspaceSeed(workspace1) === getWorkspaceSeed(workspace2);
}

function getWorkspaceSeed(workspace: Workspace): string {
	return getUUID(`NODES=${getNodesSeed(workspace.nodes)}, BANDS=${getBandsSeed(workspace.bands)}`);
}

function getNodesSeed(nodes: Node[]): string {
	return getUUID(`NODES=${nodes.map(serializeNode).sort()}`);
}

function getBandsSeed(bands: BandData[]): string {
	return getUUID(`BANDS=${bands.map(serializeBand).sort()}`);
}

/**
 * Serialize node into a string based on its properties, children, and their properties
 * @param node - node to serialize
 */
function serializeNode(node: Node): string {
	// Hashes will vary if any two nodes have different Ids, vertexIds, FriendlyNames, positions, or Children (including order)
	return getUUID(
		`${node.id}-${node.vertexId}-${node.children?.length ? node.friendlyName : ''}-@(${node.position.x},${
			node.position.y
		})-${node.children?.map((child: NodeData) => `${child.id}-${child.vertexId}`)}`
	);
}

/**
 * Serialize band data into a string based on node IDs and type
 * @param bandData - band data to serialize
 */
function serializeBand(bandData: BandData): string {
	return `${bandData.srcId}-${bandData.destId}-${bandData.type === BandType.success ? 'S' : 'F'}`;
}

/**
 * Return a list of which nodes are chained too deep and a list of which nodes are not rooted in a trigger
 * 1 - Explore all triggers and their immediate actions
 * 2 - Explore chained/on-failure actions up to the maximum depths
 * 3 - Explore and record a list of all nodes exceeding the maximum depth
 * 4 - Return the list of nodesTooDeep
 * @param graph Graph representation of workspace, containing all nodes and bands
 */
export function checkDepth(graph: Graph): [ObjID[], Map<ObjID, boolean>] {
	// Create a set of all nodes which are too deep
	const nodesTooDeep: Set<ObjID> = new Set();
	// Create a map of all node IDs -> false
	// For each explored node, this value will be changed to true
	const exploredNodes: Map<ObjID, boolean> = new Map();
	for (const node of graph.getNodes()) {
		exploredNodes.set(node.id, false);
	}

	let nodesAtCurrentDepth: Set<ObjID> = new Set();
	// Trace all triggers and their 1-step actions
	for (const node of graph.getNodes()) {
		if (node.type === NodeType.outputOnly) {
			if (graph.getBandStates(node.id).length !== 0) {
				exploredNodes.set(node.id, true); // Mark triggers as explored (if they have actions)
			}
			for (const actionLink of graph.getBandStates(node.id)) {
				exploredNodes.set(actionLink.destId, true); // Mark actions as explored
				nodesAtCurrentDepth.add(actionLink.destId);
			}
		}
	}

	// Trace through the following nodes until the maximum depth is reached
	// Create a list of the nodes just exceeding maximum depth (plus a few extras)
	const nodesExceedingLimit: Set<ObjID> = new Set();
	for (let depth = 0; depth <= Math.max(MAX_FAILOVER_DEPTH, MAX_CHAIN_DEPTH); depth++) {
		const nodesAtNextDepth: Set<ObjID> = new Set();
		Array.from(nodesAtCurrentDepth).forEach(node => {
			(graph.hasBandStates(node) ? graph.getBandStates(node) : []).forEach(dest => {
				if (dest.type$.value === BandType.success && depth >= MAX_CHAIN_DEPTH) {
					nodesExceedingLimit.add(dest.destId);
				}
				if (dest.type$.value === BandType.failure && depth >= MAX_FAILOVER_DEPTH) {
					nodesExceedingLimit.add(dest.destId);
				}
				nodesAtNextDepth.add(dest.destId);
				exploredNodes.set(dest.destId, true);
			});
		});
		nodesAtCurrentDepth = cloneDeep(nodesAtNextDepth);
	}

	const stack: ObjID[] = Array.from(nodesExceedingLimit);
	// Explore directed graph starting at unsupported depth
	// The initial nodes in the stack are roots for all nodes which exceed the maximum depth
	while (stack.length) {
		const currentId = stack.pop();
		exploredNodes.set(currentId, true);
		nodesTooDeep.add(currentId);
		(graph.hasBandStates(currentId) ? graph.getBandStates(currentId) : []).forEach(bandState => {
			// If node is unexplored, explore its path
			if (!exploredNodes.get(bandState.destId)) {
				stack.push(bandState.destId);
			}
		});
	}
	return [Array.from(nodesTooDeep), exploredNodes];
}

/**
 * Converts a NodeSet into a Node, including any node children
 * @param nodeSet - NodeSet object to be converted to Node
 */
export function convertNodeSetToNode(nodeSet: NodeSetModel): Node {
	// Convert provided position to a Point
	const position: Point = {
		...nodeSet.position,
	};

	// Convert provided NodeSet type into a NodeType
	const type: NodeType = nodeSet.type === 'Trigger' ? NodeType.outputOnly : NodeType.inputOutput;

	// Convert NodeSet to an individual node
	if (nodeSet.data.length === 1) {
		const node = lookupNodeData(nodeSet.data[0]);
		if (!nodeSet.name) {
			return {
				...node,
				id: nodeSet.id,
				type,
				position,
			};
		}
	}

	// Convert empty NodeSet to a deleted node
	if (nodeSet.data.length === 0) {
		return {
			id: nodeSet.id,
			type,
			appName: DELETED_NODE_APP_NAME,
			appId: DELETED_NODE_APP_NAME,
			nodeTitle: DELETED_NODE_NAME,
			friendlyName: DELETED_NODE_NAME,
			deleted: true,
			position,
		};
	}

	// Convert NodeSet to a group of multiple nodes
	return {
		id: nodeSet.id,
		type,
		appName: '',
		appId: '',
		nodeTitle: UNTITLED_GROUP_TEXT,
		friendlyName: nodeSet.name,
		position,
		children: nodeSet.data.map(vId => ({ ...lookupNodeData(vId), type })),
	};
}

export function workflowCardDataToWorkspace(workflow: WorkflowCardData): Workspace {
	const workspace: Workspace = cloneDeep(EMPTY_WORKSPACE);
	if (!workflow) {
		return workspace;
	}

	// Add all nodes from the model
	workspace.nodes = workflow.nodes;

	// Add all bands from the model
	const linkMap = new Graph();
	for (const band of workflow.bands || []) {
		for (const dest of band.destinations) {
			const bandType = dest.type === 'OnSuccess' ? BandType.success : BandType.failure;
			workspace.bands.push({ srcId: band.source, destId: dest.id, type: bandType });
			linkMap.addLink(band.source, { destId: dest.id, type$: null, position$: null });
		}
	}

	// Add the workflows from the model
	workspace.workflows.push({
		id: workflow.id,
		nodes: new Set(workflow.nodeSets.map(ns => ns.id)),
	});

	return workspace;
}

/**
 * Converts a Node into a NodeSet, including any children
 * @param node Node object to be converted to NodeSet
 */
export function convertNodeToNodeSet(node: Node): NodeSetModel {
	return {
		id: node.id,
		name: node.children ? node.friendlyName : null,
		type: node.type === NodeType.outputOnly ? 'Trigger' : 'Action',
		position: {
			...node.position,
		},
		data: (node.children ? node.children.map(child => child.vertexId) : [node.vertexId]).filter(vId => !!vId),
	};
}

/**
 * Function to fetch a node's information by vertexId
 * @param vId - ID of node to be looked up
 */
export function lookupNodeData(vId: ObjID): NodeData {
	return (
		availableNodes.get(vId) ?? {
			id: getUUID(),
			type: null,
			vertexId: vId,
			appName: DELETED_NODE_APP_NAME,
			appId: DELETED_NODE_APP_NAME,
			nodeTitle: DELETED_NODE_NAME,
			friendlyName: DELETED_NODE_NAME,
			deleted: true,
		}
	);
}

/**
 * Set the map of available sidetray nodes.
 * This map is used to lookup nodeData by vertexId
 * @param map - Map of available vertexIds -> NodeData
 */
export function setAvailableNodes(map: Map<ObjID, NodeData>) {
	availableNodes = map;
}

/**
 * Convert imported trigger/action models into NodeData objects
 */
function convertToNodeData(type: NodeType, product: Product, item: NodeModel, children?: NodeData[]): NodeData {
	return {
		id: getUUID(),
		vertexId: item.id,
		appName: product.name,
		appId: product.id,
		nodeTitle: item.friendlyNameStatic,
		friendlyName: item.friendlyName,
		metadataList: item.metadataList,
		deleted: item.ttl === 0 || !!item.ttl,
		segmentId: item.segmentId,
		isCritical: item.isCritical,
		location: item.location,
		type,
		children,
	};
}

/**
 * Convert a NodeModel object into a trigger
 */
export function toTrigger(item: NodeModel, product: Product, children?: NodeData[]) {
	return convertToNodeData(NodeType.outputOnly, product, item, children);
}

/**
 * Convert a NodeModel object into an action
 */
export function toAction(item: NodeModel, product: Product, children?: NodeData[]) {
	return convertToNodeData(NodeType.inputOutput, product, item, children);
}

// Copyright (c) Motorola Solutions Inc. All Rights Reserved.
